/**
 * Safe logging utility that prevents PII exposure
 * Only logs in development mode to avoid exposing sensitive data in production
 */

import { PII_EMAIL_REGEX, PII_SSN_REGEX, PII_PHONE_REGEX, PII_ZIP_CODE_REGEX } from '@utils/validators/regex';

interface LogContext {
    component?: string;
    handler?: string;
    field?: string;
    [key: string]: unknown;
}

/**
 * PII patterns for common sensitive data types
 */
const createFreshRegex = (pattern: RegExp): RegExp => {
    // Add global flag for replace operations (to replace all occurrences)
    const flags = pattern.flags.includes('g') ? pattern.flags : `${pattern.flags}g`;
    return new RegExp(pattern.source, flags);
};

/**
 * PII patterns - using functions to return fresh regex instances on each call
 * This prevents state issues from the global flag
 */
const getPIIPatterns = () => ({
    email: createFreshRegex(PII_EMAIL_REGEX),
    ssn: createFreshRegex(PII_SSN_REGEX),
    phone: createFreshRegex(PII_PHONE_REGEX),
    zipCode: createFreshRegex(PII_ZIP_CODE_REGEX),
});

/**
 * Sanitizes a string by redacting PII patterns
 * @param str - String to sanitize
 * @returns Sanitized string with PII replaced by placeholders
 */
const sanitizeString = (str: string): string => {
    if (!str || typeof str !== 'string') {
        return str;
    }
    
    const patterns = getPIIPatterns();
    return str
        .replace(patterns.email, '[EMAIL]')
        .replace(patterns.ssn, '[SSN]')
        .replace(patterns.phone, '[PHONE]')
        .replace(patterns.zipCode, '[ZIP]');
};

/**
 * Sanitizes a value recursively, handling strings, objects, and arrays
 * @param value - Value to sanitize (can be string, object, array, or primitive)
 * @param visited - Set to track circular references
 * @returns Sanitized value
 */
const sanitizeValue = (value: unknown, visited: WeakSet<object> = new WeakSet()): unknown => {
    // Handle null and undefined
    if (value === null || value === undefined) {
        return value;
    }
    
    // Handle strings - sanitize for PII
    if (typeof value === 'string') {
        return sanitizeString(value);
    }
    
    // Handle primitives (number, boolean, symbol, etc.)
    if (typeof value !== 'object') {
        return value;
    }
    
    // Handle arrays
    if (Array.isArray(value)) {
        // Check for circular reference
        if (visited.has(value)) {
            return '[Circular reference]';
        }
        visited.add(value);
        return value.map(item => sanitizeValue(item, visited));
    }
    
    // Handle objects
    // Check for circular reference
    if (visited.has(value)) {
        return '[Circular reference]';
    }
    visited.add(value);
    
    const sanitized: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(value)) {
        sanitized[key] = sanitizeValue(val, visited);
    }
    return sanitized;
};

/**
 * Sanitizes a LogContext object by sanitizing all string values
 * @param context - Context object to sanitize
 * @returns Sanitized context object
 */
const sanitizeContext = (context: LogContext): LogContext => {
    if (!context || typeof context !== 'object') {
        return context;
    }
    
    return sanitizeValue(context) as LogContext;
};

/**
 * Safely stringifies an object, handling circular references
 * @param obj - Object to stringify
 * @returns Stringified object or error message
 */
const safeStringify = (obj: unknown): string => {
    try {
        return JSON.stringify(obj, null, 2);
    } catch (error) {
        if (error instanceof TypeError && error.message.includes('circular')) {
            return '[Circular reference detected]';
        }
        return `[Stringify error: ${error instanceof Error ? error.message : 'unknown'}]`;
    }
};

/**
 * Logs an error message with context, only in development mode
 * Never logs PII or sensitive data - all inputs are sanitized
 * 
 * @param message - Error message (will be sanitized for PII)
 * @param context - Additional context (will be sanitized for PII)
 */
export const logError = (message: string, context?: LogContext): void => {
    if (process.env.NODE_ENV === 'development') {
        const safeMessage = sanitizeString(message);
        const safeContext = context ? sanitizeContext(context) : undefined;
        const contextStr = safeContext
            ? ` Context: ${safeStringify(safeContext)}`
            : '';
        console.error(`[Error] ${safeMessage}${contextStr}`);
    }
    // In production, consider sending to telemetry service without PII
    // Example: telemetry.trackError(message, { ...context, timestamp: Date.now() });
};

/**
 * Logs a warning message with context, only in development mode
 * Never logs PII or sensitive data - all inputs are sanitized
 * 
 * @param message - Warning message (will be sanitized for PII)
 * @param context - Additional context (will be sanitized for PII)
 */
export const logWarning = (message: string, context?: LogContext): void => {
    if (process.env.NODE_ENV === 'development') {
        const safeMessage = sanitizeString(message);
        const safeContext = context ? sanitizeContext(context) : undefined;
        const contextStr = safeContext
            ? ` Context: ${safeStringify(safeContext)}`
            : '';
        console.warn(`[Warning] ${safeMessage}${contextStr}`);
    }
};

/**
 * Logs an info message with context, only in development mode
 * Never logs PII or sensitive data - all inputs are sanitized
 * 
 * @param message - Info message (will be sanitized for PII)
 * @param context - Additional context (will be sanitized for PII)
 */
export const logInfo = (message: string, context?: LogContext): void => {
    if (process.env.NODE_ENV === 'development') {
        const safeMessage = sanitizeString(message);
        const safeContext = context ? sanitizeContext(context) : undefined;
        const contextStr = safeContext
            ? ` Context: ${safeStringify(safeContext)}`
            : '';
        console.info(`[Info] ${safeMessage}${contextStr}`);
    }
};

/**
 * Logs validation errors with specific field context
 * Use this for missing required fields or validation failures
 */
export const logValidationError = (
    handlerName: string,
    missingFields: string[],
    componentName?: string
): void => {
    logError(
        `Validation failed in ${handlerName}: Missing required field(s)`,
        {
            component: componentName || 'Unknown',
            handler: handlerName,
            missingFields: missingFields.join(', '),
        }
    );
};

